import { useMemo } from 'react';
import type { IsoDate, TrackerSet } from '@/domain/content.types';
import { selectSchedule } from '@/domain/schedule';
import type { SetStats } from '@/domain/selectors';
import { useT } from '@/i18n/useT';
import { phaseLabel, phaseTone } from '@/components/tracker/phaseLabel';

interface NowCardProps {
  set: TrackerSet;
  stats: SetStats;
  today: IsoDate;
}

/**
 * The first thing on the page: which phase is obtainable today, how long it
 * has left, and what comes next. Sits in the scrolling content rather than the
 * sticky header on purpose — it matters when you open the app, not while you
 * are three screens down ticking rows, and the header is already tall on a
 * phone.
 */
export function NowCard({ set, stats, today }: NowCardProps) {
  const { t, lang } = useT();
  const schedule = useMemo(() => selectSchedule(set, today), [set, today]);
  if (!schedule) return null;

  const progressOf = (groupId: string): string => {
    const g = stats.perGroup.find((x) => x.id === groupId);
    return g ? t('stats.collected', { done: g.done, total: g.total }) : '';
  };
  // Local midnight, so the 15th does not print as the 14th west of Greenwich.
  const fmtDate = (iso: IsoDate): string =>
    new Intl.DateTimeFormat(lang, { day: 'numeric', month: 'long' }).format(
      new Date(`${iso}T00:00:00`),
    );

  const label = 'font-display text-[10px] leading-4 tracking-wide uppercase';

  return (
    <section
      aria-label={t('now.live')}
      className="mb-3 flex flex-col gap-1 rounded-sm border border-pip-dim/50 bg-pip-faint/10 px-3 py-2"
    >
      {schedule.state === 'after' ? (
        <p className="text-[12px] leading-4 text-text-dim">{t('now.after')}</p>
      ) : null}

      {schedule.live.map(({ group, daysLeft }) => (
        <div key={group.id} className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className={`${label} text-pip`}>● {t('now.live')}</span>
          <a
            href={`#group-${group.id}`}
            title={t('group.jump')}
            className="text-[13px] leading-5 font-semibold text-text underline decoration-pip-dim/60 underline-offset-2 hover:text-pip"
          >
            {group.title[lang]}
          </a>
          {group.subtitle ? (
            <span className="text-[11px] leading-4 text-text-faint">{group.subtitle[lang]}</span>
          ) : null}
          <span
            className={`rounded-xs border px-1 text-[10px] leading-4 ${phaseTone({ kind: 'live', daysLeft })}`}
          >
            {phaseLabel(t, { kind: 'live', daysLeft })}
          </span>
          <span className="tnum text-[11px] leading-4 text-text-dim">{progressOf(group.id)}</span>
        </div>
      ))}

      {schedule.next ? (
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className={`${label} text-text-faint`}>{t('now.next')}</span>
          <a
            href={`#group-${schedule.next.group.id}`}
            title={t('group.jump')}
            className="text-[12px] leading-4 text-text-dim hover:text-pip"
          >
            {schedule.next.group.title[lang]}
          </a>
          {schedule.state === 'before' && schedule.next.group.starts ? (
            <span className="text-[11px] leading-4 text-text-faint">
              {t('now.before', { date: fmtDate(schedule.next.group.starts) })}
            </span>
          ) : null}
          <span className="rounded-xs border border-line px-1 text-[10px] leading-4 text-text-faint">
            {phaseLabel(t, { kind: 'upcoming', startsIn: schedule.next.startsIn })}
          </span>
        </div>
      ) : null}
    </section>
  );
}
